import { type CollectionChange, type Insertion, type Move, ObservableCollection, type ReconciliationStep, type Removal, type Replacement } from '@acies/observables';
import Container from './container/container';
import DefaultListItemRenderer from './default-list-item-renderer';
import InteractiveContainerEvent from './interactive-container-event';
import ListItemRenderer from './list-item-renderer';

/**
 * The List component main purpose, is to show a visual representation of a data set.
 *
 * This data set can be either an Array or an ObservableCollection.
 *
 * The List is headless, so you can define exactly what you want to show per data set item.
 *
 * You use the data property to set the data that will be rendered and the ItemRenderer property as the class factory you want to use as the visual representation of a single item in the data set.
 *
 * Nothing here is selected. A List draws rows, keeps them in step with the data behind them, and moves the focus between them with the arrow keys — a list the user picks from is a {@link SelectList}, which is this with a selection on top.
 */
export default class List<Item> extends Container {
	/**
	 * The data property is the data source for the List.
	 *
	 * @param value An Array or an ObservableCollection as the data item set to render individualy. You can pass null, that will clear the List.
	 */
	public set data(value: Item[] | null | ObservableCollection<Item>) {
		if (this.#data === value) {
			return;
		}
		assertIsValidData(value);
		// if we already have a ObservableCollection as data
		// we need to unsubscribe so we don't get memory leaks
		if (this.#data instanceof ObservableCollection) {
			this.#data.unsubscribe(this.invalidateData);
		}

		this.#data = value;

		if (this.#data instanceof ObservableCollection) {
			this.#data.subscribe(this.invalidateData);
		}
		this.#dataChanged = true;
		this.invalidateProperties();
	}

	public get data(): Item[] | null | ObservableCollection<Item> {
		return this.#data;
	}

	/**
	 * The ItemRenderer property defines the component class definbition you want to visually represent each of the items in the data set.
	 *
	 * If the ItemRenderer is not defined, a fallback DefaultListItemRenderer is provided so you get some confirmation that the List is showing data set items correctly.
	 *
	 * When you create your ItemRenderer, you must extend the ListItemRenderer class.
	 */
	public set ItemRenderer(value: new () => ListItemRenderer<Item>) {
		assertIsValidItemRenderer(value);
		this.#itemRenderer = value;
		this.#itemRendererChanged = true;
		this.invalidateProperties();
	}

	public get ItemRenderer(): new () => ListItemRenderer<Item> {
		return this.#itemRenderer;
	}

	#currentlyFocusedItemRenderer: null | ListItemRenderer<Item>;
	#data: Item[] | null | ObservableCollection<Item>;
	#dataChanged: boolean;
	#itemRenderer: new () => ListItemRenderer<Item>;
	#itemRendererChanged: boolean;
	#itemRendererClassChanged: boolean;
	// performance tests with 500 data set items, shows recreating instances from pool
	// goes from 35ms to 8ms on a fast desktop developement machine
	#itemRendererPool: Array<ListItemRenderer<Item>>;
	#itemToRendererMap: Map<Item, ListItemRenderer<Item>>;

	/** The ItemRenderer the keyboard is on, or null when the focus is somewhere else entirely. */
	protected get currentlyFocusedItemRenderer(): null | ListItemRenderer<Item> {
		return this.#currentlyFocusedItemRenderer;
	}

	public constructor() {
		super();
		this.#currentlyFocusedItemRenderer = null;
		this.#data = null;
		this.#dataChanged = false;
		this.#itemRenderer = DefaultListItemRenderer;
		this.#itemRendererChanged = true;
		this.#itemRendererClassChanged = false;
		this.#itemRendererPool = [];
		this.#itemToRendererMap = new Map();
		// the update listener method will be invoked in the scope of the
		// ObservableCollection, so we re-scope here to this
		this.invalidateData = this.invalidateData.bind(this);
		this.keyboardListener = this.keyboardListener.bind(this);
		this.autoLayout = 'vertical';
		this.clipContent = true;
		this.gapVertical = 8;
		this.paddingHorizontal = 8;
		this.paddingVertical = 8;
	}

	/**
	 * Brings the row an Item is rendered in into view, without touching the focus.
	 *
	 * For showing something the list was handed rather than something the user reached: a row added while the user was looking elsewhere, or one pointed at from outside the list. Focusing a row scrolls it into view too, and that is the right way when the user is meant to act on it — this is the way when they are only
	 * meant to see it.
	 *
	 * Scrolled to `nearest`, so a row already in view is left where it is and one just outside comes in by as little as it takes. The alternative pulls rows that were fine to the middle of the list, which reads as the list jumping for no reason.
	 *
	 * An Item the list is not rendering is not an error and does nothing: it may have been filtered out, or replaced, or handed over before the ItemRenderers were made. A caller asking for a row to be seen is asking for what can be seen.
	 */
	public scrollToItem(item: Item): void {
		const itemRenderer = this.#itemToRendererMap.get(item);
		if (itemRenderer === undefined) {
			return;
		}
		itemRenderer.scrollIntoView({ block: 'nearest' });
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#itemRendererChanged) {
			this.#commitItemRenderer();
		}
		if (this.#dataChanged) {
			this.#commitData();
		}
	}

	/**
	 * Invoked once the ItemRenderers have been built from the data and added to the List.
	 *
	 * The hook a subclass hangs whatever has to be true of a freshly built list on. Nothing here, because a List has nothing to say about a set of rows beyond having drawn them.
	 */
	protected dataCommitted(): void {
		// override in sub classes
	}

	/**
	 * Invoked after an arrow key has moved the focus from one ItemRenderer to the next.
	 *
	 * After and not before, because moving the focus is what decides which ItemRenderer this is about: focusing a sibling blurs the one that had the focus and then focuses the new one, both synchronously, so {@link List.currentlyFocusedItemRenderer} is the new one by the time this runs.
	 */
	protected focusMovedByKeyboard(): void {
		// override in sub classes
	}

	protected override initialize(): void {
		super.initialize();
		this.addEventListener(InteractiveContainerEvent.FOCUSED, this.#itemRendererFocused as EventListener);
		this.addEventListener(InteractiveContainerEvent.BLURRED, this.#itemRendererBlurred as EventListener);
	}

	/**
	 * We can easily invalidate the data by changing the #dataChanged to true and invalidate with invalidateProperties()
	 *
	 * An ObservableCollection that replaced one item says so, and a replacement can often be shown by handing the new item to the one ItemRenderer that was drawing the old one. That is tried first, since invalidating the data throws away every ItemRenderer and builds the list again.
	 */
	protected invalidateData(change?: CollectionChange<Item>): void {
		if (change !== undefined && this.#show(change)) {
			return;
		}
		this.#dataChanged = true;
		this.invalidateProperties();
	}

	/** The ItemRenderer drawing an Item, or undefined for an Item the List is not rendering. */
	protected itemRendererFor(item: Item): ListItemRenderer<Item> | undefined {
		return this.#itemToRendererMap.get(item);
	}

	/**
	 * Invoked when an ItemRenderer has been handed a replacement for the Item it was drawing, in place of the List being built again.
	 *
	 * Nothing here: a List holds nothing about an Item beyond the row drawing it, and that row has just been told. A subclass that keeps something of its own by Item has to move it across.
	 */
	protected itemReplaced(_current: Item, _replacement: Item, _itemRenderer: ListItemRenderer<Item>): void {
		// override in sub classes
	}

	/**
	 * Invoked when an ItemRenderer has been put in for an Item that joined the collection, in place of the List being built again.
	 *
	 * Nothing here, for the reason {@link List.itemReplaced} gives. A subclass that keeps something per row — which row is the tab stop, say — has to take the new one into account.
	 */
	protected itemInserted(_item: Item, _itemRenderer: ListItemRenderer<Item>): void {
		// override in sub classes
	}

	/**
	 * Invoked just before an ItemRenderer is recycled and taken out, because its Item left the collection, in place of the List being built again.
	 *
	 * Nothing here, for the reason {@link List.itemReplaced} gives. A subclass that keeps something of its own by Item has to let go of it.
	 */
	protected itemRemoved(_item: Item, _itemRenderer: ListItemRenderer<Item>): void {
		// override in sub classes
	}

	/**
	 * Puts an ItemRenderer back to the state a fresh one is in, on its way into the instance pool.
	 *
	 * Whatever the row is carrying is still on it when it comes back out, so anything a subclass puts on a row belongs here as well.
	 */
	protected recycleItemRenderer(itemRenderer: ListItemRenderer<Item>): void {
		itemRenderer.dispose();
		itemRenderer.item = null;
	}

	/** Every ItemRenderer the List is currently drawing, in the order they are drawn. */
	protected itemRenderers(): Array<ListItemRenderer<Item>> {
		const itemRenderers: Array<ListItemRenderer<Item>> = [];
		for (const child of this.children) {
			assertIsListItemRenderer<Item>(child);
			itemRenderers.push(child);
		}
		return itemRenderers;
	}

	#addDataItemRenderers(): void {
		if (this.#data instanceof ObservableCollection) {
			this.#addItemRenderers(this.#data.collection);
			return;
		}
		if (Array.isArray(this.#data)) {
			this.#addItemRenderers(this.#data);
		}
	}

	#addItemRendererInstancesToPool(): void {
		for (const itemRenderer of this.children) {
			assertIsListItemRenderer<Item>(itemRenderer);
			if (this.#itemRendererPool.includes(itemRenderer) === false) {
				this.#letGoOfFocusOn(itemRenderer);
				this.recycleItemRenderer(itemRenderer);
				this.#itemRendererPool.push(itemRenderer);
			}
		}
	}

	#addItemRenderers(items: Item[]): void {
		const itemRenderers: Array<ListItemRenderer<Item>> = [];
		for (const item of items) {
			const itemRenderer = this.#itemRendererFromPool();
			itemRenderer.item = item;
			itemRenderers.push(itemRenderer);
			this.#itemToRendererMap.set(item, itemRenderer);
		}
		this.addComponents(itemRenderers);
	}

	/** The first ItemRenderer in the pool, or a new one when the pool is empty. */
	#itemRendererFromPool(): ListItemRenderer<Item> {
		const itemRenderer: ListItemRenderer<Item> | undefined = this.#itemRendererPool.splice(0, 1)[0] ?? new this.ItemRenderer();
		assertIsListItemRenderer(itemRenderer);
		return itemRenderer;
	}

	#addKeyboardListeners(): void {
		window.addEventListener('keydown', this.keyboardListener);
	}

	#commitData(): void {
		this.#dataChanged = false;
		this.#removeDataItemRenderers();
		// here rather than where the class changed, which is the whole of the fix: the rows on screen
		// at that moment had not been pooled yet, so emptying the pool there emptied it just in time
		// for those same rows to be put into it and taken straight back out again, and a List asked
		// for a new kind of row went on showing the old kind
		if (this.#itemRendererClassChanged) {
			this.#itemRendererClassChanged = false;
			this.#itemRendererPool = [];
		}
		if (this.data) {
			this.#createAndAddtemRenderersFromData();
		}
		this.dataCommitted();
	}

	#commitItemRenderer(): void {
		this.#itemRendererChanged = false;
		// the pooled rows are of the class we have just moved off, so they are of no use to the one we
		// have moved to — but they cannot be thrown away yet, see #commitData
		this.#itemRendererClassChanged = true;
		// invalidate the data property that will use the changed ItemRenderer to render the data Items
		this.#dataChanged = true;
	}

	#createAndAddtemRenderersFromData(): void {
		this.#removeDataItemRenderers();
		this.#addDataItemRenderers();
	}

	#focusNextItemRenderer(): void {
		assertIsListItemRenderer(this.#currentlyFocusedItemRenderer);
		// the nextElementSibling could be null if #currentlyFocusedItemRenderer
		// is the last index child, so check for null and return early if that's the case
		if (this.#currentlyFocusedItemRenderer.nextElementSibling === null) {
			return;
		}
		// otherwise assert and focus the ItemRenderer
		assertIsListItemRenderer(this.#currentlyFocusedItemRenderer.nextElementSibling);
		this.#currentlyFocusedItemRenderer.nextElementSibling.focus();
	}

	#focusPreviousItemRenderer(): void {
		assertIsListItemRenderer(this.#currentlyFocusedItemRenderer);
		// the previousElementSibling could be null if #currentlyFocusedItemRenderer
		// is the first index child, so check for null and return early if that's the case
		if (this.#currentlyFocusedItemRenderer.previousElementSibling === null) {
			return;
		}
		// otherwise assert and focus the ItemRenderer
		assertIsListItemRenderer(this.#currentlyFocusedItemRenderer.previousElementSibling);
		this.#currentlyFocusedItemRenderer.previousElementSibling.focus();
	}

	#isDirectChild(target: EventTarget | null): boolean {
		return target instanceof Node && target.parentElement === this;
	}

	#itemRendererBlurred(event: InteractiveContainerEvent): void {
		event.stopImmediatePropagation();
		this.#letGoOfFocus();
	}

	#letGoOfFocus(): void {
		this.#currentlyFocusedItemRenderer = null;
		this.#removeKeyboardListeners();
	}

	/**
	 * Lets go of a row on its way out of the List, if it is the one the keyboard is on.
	 *
	 * Chromium blurs a focused element that is removed, and the BLURRED that follows would do this anyway, harmlessly a second time. A browser that does not blur on removal says nothing, and the BLURRED the row dispatches once it is out no longer bubbles here — so the List would go on holding a pooled row as focused
	 * and listening on the window for the arrow keys, and a SelectList would select a row nobody can see on the next one. The row cannot see to this itself, which is why InteractiveContainer leaves it to the parent that takes it out.
	 */
	#letGoOfFocusOn(itemRenderer: ListItemRenderer<Item>): void {
		if (itemRenderer === this.#currentlyFocusedItemRenderer) {
			this.#letGoOfFocus();
		}
	}

	#itemRendererFocused(event: InteractiveContainerEvent): void {
		/**
		 * We only stop the Event propagation if the Event has a direct child as the dispacther(target).
		 *
		 * Inside the ItemRenderer, we could have children that dispatch Events that should be able to bubble up through the List and be caught higher up the DOM tree.
		 */
		if (this.#isDirectChild(event.target)) {
			event.stopImmediatePropagation();
			assertIsListItemRenderer<Item>(event.target);
			this.#currentlyFocusedItemRenderer = event.target;
			this.#addKeyboardListeners();
		}
	}

	/**
	 * Hands a replacement to the ItemRenderer that was drawing the item it replaced.
	 *
	 * Returns false when it cannot be done from here, which leaves the caller to invalidate the data and build the list again. That is the case when the replaced item was not being drawn, because the filter left it out, and when the replacement does not land where the item it replaced was being drawn, because the sort
	 * or the filter moved it: getting the children back into the collection order is what a full pass is for.
	 */
	#replaceItemRenderer({ current, index, replacement }: Replacement<Item>): boolean {
		const itemRenderer = this.#itemToRendererMap.get(current);
		if (itemRenderer === undefined) {
			return false;
		}
		if (this.children.item(index) !== itemRenderer) {
			return false;
		}
		this.#itemToRendererMap.delete(current);
		this.#itemToRendererMap.set(replacement, itemRenderer);
		itemRenderer.item = replacement;
		this.itemReplaced(current, replacement, itemRenderer);
		return true;
	}

	#removeDataItemRenderers(): void {
		this.#addItemRendererInstancesToPool();
		this.#itemToRendererMap.clear();
		this.removeAllComponents();
	}

	/**
	 * Shows what one change changed without building the list again, or answers false when that cannot be done from here and the list has to be built again from the collection.
	 *
	 * A reconciliation is taken step by step, each step on the rows as the steps before it left them — see `Reconciliation` for why the indexes are right that way. A step that cannot be taken ends it: the rows are then out of step with the collection, and building the list again is what puts them back.
	 */
	#show(change: CollectionChange<Item>): boolean {
		if (change.kind === 'replacement') {
			return this.#replaceItemRenderer(change);
		}
		return change.steps.every((step) => this.#take(step));
	}

	#take(step: ReconciliationStep<Item>): boolean {
		switch (step.kind) {
			case 'insertion':
				return this.#insertItemRenderer(step);
			case 'move':
				return this.#moveItemRenderer(step);
			case 'removal':
				return this.#removeItemRenderer(step);
			case 'replacement':
				return this.#replaceItemRenderer(step);
		}
	}

	/** Puts a row in for an item that joined, in front of the row holding its index, so no other row leaves the document. */
	#insertItemRenderer({ index, item }: Insertion<Item>): boolean {
		if (index > this.children.length) {
			return false;
		}
		const itemRenderer = this.#itemRendererFromPool();
		itemRenderer.item = item;
		this.#itemToRendererMap.set(item, itemRenderer);
		this.addComponentAt(index, itemRenderer);
		this.itemInserted(item, itemRenderer);
		return true;
	}

	/**
	 * Moves the row drawing an item to its new index, keeping the row's state — see Container.moveComponentBefore.
	 *
	 * The row to move in front of is the one that will hold the index once this row is out of the way: the one at the index when the row moves up, and the one after it when the row moves down, since everything between shifts back by one. Null is the end.
	 */
	#moveItemRenderer({ from, item, to }: Move<Item>): boolean {
		const itemRenderer = this.#itemToRendererMap.get(item);
		if (itemRenderer === undefined || this.children.item(from) !== itemRenderer) {
			return false;
		}
		const before = this.children.item(to < from ? to : to + 1);
		if (before === null) {
			this.moveComponentBefore(itemRenderer, null);
			return true;
		}
		// every child of a List is a row, so anything else is a List that has been tampered with
		assertIsListItemRenderer<Item>(before);
		this.moveComponentBefore(itemRenderer, before);
		return true;
	}

	/** Takes the row drawing an item that left out, and recycles it the way a full pass would: told, disposed, emptied and pooled, and only then out of the document. */
	#removeItemRenderer({ index, item }: Removal<Item>): boolean {
		const itemRenderer = this.#itemToRendererMap.get(item);
		if (itemRenderer === undefined || this.children.item(index) !== itemRenderer) {
			return false;
		}
		this.#itemToRendererMap.delete(item);
		this.#letGoOfFocusOn(itemRenderer);
		this.itemRemoved(item, itemRenderer);
		this.recycleItemRenderer(itemRenderer);
		this.#itemRendererPool.push(itemRenderer);
		this.removeComponent(itemRenderer);
		return true;
	}

	#removeKeyboardListeners(): void {
		window.removeEventListener('keydown', this.keyboardListener);
	}

	/**
	 * Moves the focus with the arrow keys.
	 *
	 * An arrow key we acted on has its default prevented, and only then: a list clips its content, so it is a scroll container, and an arrow would otherwise move the focus one row and scroll the list on top of that. InteractiveContainer does the same for Space, and says why — pressing it inside a container that
	 * overflows scrolls the content away. This is that, one key further on. It is not something the test runner can show: an arrow key dispatched by userEvent scrolls nothing at all in it, so no test here can tell the two apart.
	 */
	protected keyboardListener(event: KeyboardEvent): void {
		if (event.code === 'ArrowDown') {
			this.#focusNextItemRenderer();
		} else if (event.code === 'ArrowUp') {
			this.#focusPreviousItemRenderer();
		} else {
			return;
		}
		event.preventDefault();
		this.focusMovedByKeyboard();
	}
}
customElements.define('ac-list', List);

export function assertIsListItemRenderer<Item>(value: unknown): asserts value is ListItemRenderer<Item> {
	if (value instanceof ListItemRenderer) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be an instance of ListItemRenderer`);
}

export function assertIsValidData(value: unknown): asserts value is Array<unknown> | null | ObservableCollection<unknown> {
	if (Array.isArray(value)) {
		// a Set can not have the same value or reference more than once
		// so we use Set size to compare against the number of items in the Array
		// if they are not exactly the same, we have duplicates and throw the TypeError
		if (new Set(value).size === value.length) {
			return;
		}
		throw new TypeError(`[${String(value)}] is invalid, items in the Array has to be unique values or references`);
	}
	if (value === null || value instanceof ObservableCollection) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be an Array, null or an instance of ObservableCollection`);
}

export function assertIsValidItemRenderer(value: unknown): asserts value is new () => ListItemRenderer<unknown> {
	// to test if value is a class definition, we need to test for typeof is 'function' first
	// and then use toString() to see if value is a class definition with startsWith('class ')
	// this is the best we can do when testing for class definitions
	if (typeof value === 'function' && value.toString().startsWith('class ')) {
		// test if the item property is in the class definition so we can be sure it exists
		if ('item' in value.prototype) {
			return;
		}
	}
	throw new TypeError(`[${String(value)}] is invalid, be a sub class definition of the ListItemRenderer class`);
}
